'use client'

import type { Header } from '@/payload-types'

import { CMSLink } from '@/components/Link'
import { Button } from '@/components/ui/button'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useAuth } from '@/providers/Auth'
import Image from 'next/image'
import { MenuIcon } from 'lucide-react'
import { Link, usePathname } from '@/i18n/routing'
import { useSearchParams } from 'next/navigation'
import React, { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'

interface Props {
  menu: Header['navItems']
}

type NavLinkField = {
  type?: ('reference' | 'custom') | null
  url?: string | null
  reference?: {
    relationTo: string
    value: unknown
  } | null
}

// CODE-04 (Wave 2): shared with HeaderNav. See src/utilities/categoryHref.ts.
import { getCategoryHref } from '@/utilities/categoryHref'

function resolveUrl(
  link: NavLinkField,
  categoryId?: { id?: string | number } | number | null,
): string {
  if (categoryId !== undefined && categoryId !== null) {
    return getCategoryHref(categoryId, link.url)
  }
  if (
    link.type === 'reference' &&
    link.reference &&
    typeof link.reference.value === 'object' &&
    link.reference.value !== null &&
    'slug' in (link.reference.value as object)
  ) {
    const slug = (link.reference.value as { slug?: string | null }).slug
    if (slug) return `/${slug}`
  }
  return link.url || '#'
}

export function MobileMenu({ menu }: Props) {
  const { user } = useAuth()
  const t = useTranslations('common')

  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isOpen, setIsOpen] = useState(false)

  const closeMobileMenu = () => setIsOpen(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen])

  useEffect(() => {
    setIsOpen(false)
  }, [pathname, searchParams])

  return (
    <Sheet onOpenChange={setIsOpen} open={isOpen}>
      <SheetTrigger className="relative flex h-11 w-11 items-center justify-center rounded-full text-white transition-all hover:scale-110 duration-200 group dark:text-white">
        <MenuIcon className="h-5 w-5 group-hover:text-[var(--brand-gold)]" />
      </SheetTrigger>

      <SheetContent side="left" className="px-4 overflow-y-auto">
        <SheetHeader className="px-0 pt-4 pb-0">
          <SheetTitle className="flex items-center">
            <Image
              src="/smoking-logo.svg"
              alt="Smoking Austria"
              width={400}
              height={120}
              className="h-10 w-auto object-contain"
              priority
            />
          </SheetTitle>

          <SheetDescription />
        </SheetHeader>

        <div className="py-4">
          {menu?.length ? (
            <Accordion type="multiple" className="w-full">
              {menu.map((item, i) => {
                const hasSubNav = item.subNavItems && item.subNavItems.length > 0
                const itemUrl = resolveUrl(item.link, item.categoryId)

                if (hasSubNav) {
                  return (
                    <AccordionItem value={`item-${i}`} key={item.id} className="border-b-0">
                      <AccordionTrigger className="py-2 hover:no-underline font-mono font-medium uppercase tracking-tight hover:text-[var(--brand-gold)] data-[state=open]:text-[var(--brand-gold)]">
                        {item.link.label}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-4 flex flex-col gap-2">
                          {/* "View All" link for the top-level category */}
                          <Link
                            href={itemUrl !== '#' ? itemUrl : '/shop'}
                            className="py-2 font-mono text-sm font-semibold uppercase tracking-tight text-[var(--brand-gold)] hover:underline"
                            onClick={closeMobileMenu}
                          >
                            {t('viewAll')} — {item.link.label}
                          </Link>
                          {(!item.subNavItems || item.subNavItems.length === 0) && (
                            <span className="py-2 text-sm text-muted-foreground font-mono">
                              Keine Unterkategorien vorhanden
                            </span>
                          )}
                          {item.subNavItems?.map((subItem, j) => {
                            const hasSubSubNav =
                              subItem.subSubNavItems && subItem.subSubNavItems.length > 0
                            const subItemUrl = resolveUrl(subItem.link, subItem.categoryId)

                            if (hasSubSubNav) {
                              return (
                                <Accordion
                                  type="multiple"
                                  className="w-full"
                                  key={subItem.id}
                                >
                                  <AccordionItem
                                    value={`subitem-${j}`}
                                    className="border-b-0"
                                  >
                                    <AccordionTrigger className="py-2 hover:no-underline font-mono text-sm uppercase tracking-tight hover:text-[var(--brand-gold)] data-[state=open]:text-[var(--brand-gold)]">
                                      {subItem.link.label}
                                    </AccordionTrigger>
                                    <AccordionContent>
                                      <div className="pl-4 flex flex-col gap-2">
                                        {/* "View All" for this sub-category */}
                                        <Link
                                          href={subItemUrl !== '#' ? subItemUrl : '/shop'}
                                          className="py-2 font-mono text-xs font-semibold uppercase tracking-tight text-[var(--brand-gold)] hover:underline"
                                          onClick={closeMobileMenu}
                                        >
                                          {t('viewAll')}
                                        </Link>
                                        {subItem.subSubNavItems?.map((subSubItem) => {
                                          const subSubItemUrl = resolveUrl(subSubItem.link, subSubItem.categoryId)
                                          return (
                                            <Link
                                              href={subSubItemUrl}
                                              key={subSubItem.id}
                                              className="py-2 text-sm font-sans text-muted-foreground hover:text-[var(--brand-gold)]"
                                              onClick={closeMobileMenu}
                                            >
                                              {subSubItem.link.label}
                                            </Link>
                                          )
                                        })}
                                      </div>
                                    </AccordionContent>
                                  </AccordionItem>
                                </Accordion>
                              )
                            }
                            return (
                              <Link
                                href={subItemUrl}
                                key={subItem.id}
                                className="py-2 font-mono text-sm uppercase tracking-tight hover:text-[var(--brand-gold)]"
                                onClick={closeMobileMenu}
                              >
                                {subItem.link.label}
                              </Link>
                            )
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )
                }

                return (
                  <div key={item.id} className="py-2 border-b last:border-b-0">
                    <Link
                      href={itemUrl}
                      className="flex flex-1 items-center justify-between py-2 font-mono font-medium uppercase tracking-tight transition-all hover:text-[var(--brand-gold)]"
                      onClick={closeMobileMenu}
                    >
                      {item.link.label}
                    </Link>
                  </div>
                )
              })}
            </Accordion>
          ) : null}
        </div>

        {user ? (
          <div className="mt-4">
            <h2 className="text-xl mb-4">My account</h2>
            <hr className="my-2" />
            <ul className="flex flex-col gap-2">
              <li>
                <Link href="/orders">Orders</Link>
              </li>
              <li>
                <Link href="/account/addresses">Addresses</Link>
              </li>
              <li>
                <Link href="/account">Manage account</Link>
              </li>
              <li className="mt-6">
                <Button asChild variant="outline">
                  <Link href="/logout">Log out</Link>
                </Button>
              </li>
            </ul>
          </div>
        ) : (
          <div>
            <h2 className="text-xl mb-4">My account</h2>
            <div className="flex flex-col gap-4 mt-4">
              <Button asChild className="w-full" variant="outline">
                <Link href="/login">Log in</Link>
              </Button>
              <Button asChild className="w-full">
                <Link href="/create-account">Create an account</Link>
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
